import { Suspense, lazy, type ComponentType, type ReactNode } from 'react'
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { LoadingState } from '../design/States'
import { loadPage } from './loadPage'

/**
 * A route page, loaded on first visit rather than in the entry chunk.
 *
 * The pages are named exports, and `React.lazy` wants a module whose default is the component, so
 * this picks the named one out. Keyed by name rather than by a second import of the same module so
 * a renamed export fails the type check here instead of rendering nothing at run time.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each page has its own props, none required
function lazyPage<K extends string>(load: () => Promise<Record<K, ComponentType<any>>>, name: K) {
  return lazy(() => loadPage(load).then((module) => ({ default: module[name] })))
}

const VncPage = lazyPage(() => import('../features/connect/VncPage'), 'VncPage')
const SshPage = lazyPage(() => import('../features/connect/SshPage'), 'SshPage')
const VideoPage = lazyPage(() => import('../features/connect/VideoPage'), 'VideoPage')
const ProcessPage = lazyPage(() => import('../features/connect/ProcessPage'), 'ProcessPage')
const FilePage = lazyPage(() => import('../features/connect/FilePage'), 'FilePage')
const DockerPage = lazyPage(() => import('../features/connect/DockerPage'), 'DockerPage')
const DevicesPage = lazyPage(() => import('../features/devices/DevicesPage'), 'DevicesPage')
const DeviceDetailsPage = lazyPage(
  () => import('../features/devices/DeviceDetailsPage'),
  'DeviceDetailsPage',
)
const UsersPage = lazyPage(() => import('../features/admin/UsersPage'), 'UsersPage')
const RolesPage = lazyPage(() => import('../features/admin/RolesPage'), 'RolesPage')
const OrganizationsPage = lazyPage(
  () => import('../features/admin/OrganizationsPage'),
  'OrganizationsPage',
)
const LocationGroupsPage = lazyPage(
  () => import('../features/admin/LocationGroupsPage'),
  'LocationGroupsPage',
)
const RehomePage = lazyPage(() => import('../features/admin/RehomePage'), 'RehomePage')
const BrandsPage = lazyPage(() => import('../features/admin/BrandsPage'), 'BrandsPage')
const DeviceTypesPage = lazyPage(
  () => import('../features/admin/DeviceTypesPage'),
  'DeviceTypesPage',
)
const PrintersPage = lazyPage(() => import('../features/admin/PrintersPage'), 'PrintersPage')
const SitesPage = lazyPage(() => import('../features/sites/SitesPage'), 'SitesPage')
const LocationsPage = lazyPage(() => import('../features/admin/LocationsPage'), 'LocationsPage')
const AdminDevicesPage = lazyPage(
  () => import('../features/admin/AdminDevicesPage'),
  'AdminDevicesPage',
)
const ServerSettingsPage = lazyPage(
  () => import('../features/admin/ServerSettingsPage'),
  'ServerSettingsPage',
)
const AuthorizationAuditLogPage = lazyPage(
  () => import('../features/admin/AuthorizationAuditLogPage'),
  'AuthorizationAuditLogPage',
)
const AuthorizationDashboardPage = lazyPage(
  () => import('../features/admin/AuthorizationDashboardPage'),
  'AuthorizationDashboardPage',
)
const JobsPage = lazyPage(() => import('../features/jobs/JobsPage'), 'JobsPage')
const JobQueuePage = lazyPage(() => import('../features/jobs/JobQueuePage'), 'JobQueuePage')
const JobRunsPage = lazyPage(() => import('../features/jobs/JobRunsPage'), 'JobRunsPage')
const JobSchedulesPage = lazyPage(
  () => import('../features/jobs/JobSchedulesPage'),
  'JobSchedulesPage',
)
const JobVariablesPage = lazyPage(
  () => import('../features/jobs/JobVariablesPage'),
  'JobVariablesPage',
)
const JobApprovalsPage = lazyPage(
  () => import('../features/jobs/JobApprovalsPage'),
  'JobApprovalsPage',
)
const JobEditorPage = lazyPage(() => import('../features/jobs/JobEditorPage'), 'JobEditorPage')
const FirmwarePage = lazyPage(() => import('../features/software/FirmwarePage'), 'FirmwarePage')
const WindowsUpdatesPage = lazyPage(
  () => import('../features/software/WindowsUpdatesPage'),
  'WindowsUpdatesPage',
)
const AgentVersionsPage = lazyPage(
  () => import('../features/software/AgentVersionsPage'),
  'AgentVersionsPage',
)
const RolloutsPage = lazyPage(() => import('../features/rollouts/RolloutsPage'), 'RolloutsPage')
const RolloutDetailPage = lazyPage(
  () => import('../features/rollouts/RolloutDetailPage'),
  'RolloutDetailPage',
)
const RolloutPlansPage = lazyPage(
  () => import('../features/rollouts/RolloutPlansPage'),
  'RolloutPlansPage',
)
const SystemLogsPage = lazyPage(() => import('../features/logs/SystemLogsPage'), 'SystemLogsPage')
const MapPage = lazyPage(() => import('../features/map/MapPage'), 'MapPage')
const DashboardPage = lazyPage(() => import('../features/dashboard/DashboardPage'), 'DashboardPage')
const SessionsPage = lazyPage(() => import('../features/sessions/SessionsPage'), 'SessionsPage')
const GroupsPage = lazyPage(() => import('../features/groups/GroupsPage'), 'GroupsPage')
const GroupEditorPage = lazyPage(
  () => import('../features/groups/GroupEditorPage'),
  'GroupEditorPage',
)
const GroupDetailPage = lazyPage(
  () => import('../features/groups/GroupDetailPage'),
  'GroupDetailPage',
)
const ActivityPage = lazyPage(() => import('../features/activity/ActivityPage'), 'ActivityPage')
const AlertsPage = lazyPage(() => import('../features/alerts/AlertsPage'), 'AlertsPage')
const IncidentPage = lazyPage(() => import('../features/alerts/IncidentPage'), 'IncidentPage')
const NotificationSettingsPage = lazyPage(
  () => import('../features/alerts/NotificationSettingsPage'),
  'NotificationSettingsPage',
)
const AlertingSetupPage = lazyPage(() => import('../features/alerts/AlertingSetupPage'), 'AlertingSetupPage')
const SettingsProfilesPage = lazyPage(
  () => import('../features/settings/SettingsProfilesPage'),
  'SettingsProfilesPage',
)
const ProfileEditorPage = lazyPage(
  () => import('../features/settings/ProfileEditorPage'),
  'ProfileEditorPage',
)
const SettingExplorerPage = lazyPage(
  () => import('../features/settings/SettingExplorerPage'),
  'SettingExplorerPage',
)
const CompliancePage = lazyPage(
  () => import('../features/settings/CompliancePage'),
  'CompliancePage',
)
const LocationPage = lazyPage(() => import('../features/settings/LocationPage'), 'LocationPage')
const ClaimsPage = lazyPage(() => import('../features/provisioning/ClaimsPage'), 'ClaimsPage')
const SupportDownloadsPage = lazyPage(
  () => import('../features/downloads/SupportDownloadsPage'),
  'SupportDownloadsPage',
)
const AddDevicesPage = lazyPage(
  () => import('../features/provisioning/AddDevicesPage'),
  'AddDevicesPage',
)
const RegistrationsPage = lazyPage(
  () => import('../features/provisioning/RegistrationsPage'),
  'RegistrationsPage',
)
const QueuePage = lazyPage(() => import('../features/provisioning/QueuePage'), 'QueuePage')
const EnrollmentActivityPage = lazyPage(
  () => import('../features/provisioning/EnrollmentActivityPage'),
  'EnrollmentActivityPage',
)
const PairingPage = lazyPage(() => import('../features/provisioning/PairingPage'), 'PairingPage')
const TypeRulesPage = lazyPage(
  () => import('../features/provisioning/TypeRulesPage'),
  'TypeRulesPage',
)
const ApiKeysPage = lazyPage(() => import('../features/provisioning/ApiKeysPage'), 'ApiKeysPage')
const IntegrationApiKeysPage = lazyPage(() => import('../features/admin/IntegrationApiKeysPage'), 'IntegrationApiKeysPage')

/**
 * The Relay module's own routes.
 *
 * Deliberately free of chrome. This component is rendered in two places: inside this portal's own
 * AppShell when Relay is deployed standalone, and inside the AboveStore shell's layout when a
 * customer runs both products. Neither host wants a second header, so the header belongs to the
 * shell and never to the module.
 *
 * Every page is behind `React.lazy`, so the entry chunk carries the shell and the route table and
 * each page's code (and its heavy dependencies: ApexCharts, MapLibre, xterm) arrives on first visit.
 * One Suspense boundary answers for all of them with the same page-level loading state, so a page
 * still downloading looks exactly like a page still fetching its data.
 */

/** What a page shows while its code is still arriving. `data-route-loading` is for the tests. */
export function RouteLoading() {
  return (
    <div data-route-loading="">
      <LoadingState />
    </div>
  )
}

/**
 * The Firmware page's old address, or its old Windows updates tab. Firmware and Windows updates
 * were one page with two tabs (and, before that, /settings/software); they are two pages now, so
 * `?tab=windows-updates` goes to the Windows updates page and anything else to Firmware. The tab
 * is dropped; any other query is carried over.
 */
function FirmwareTabRedirect({ page }: { page?: ReactNode }) {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const tab = params.get('tab')
  params.delete('tab')
  const rest = params.toString() ? `?${params.toString()}` : ''
  if (tab === 'windows-updates') return <Navigate to={`/settings/windows-updates${rest}`} replace />
  if (page && tab === null) return <>{page}</>
  return <Navigate to={`/settings/firmware${rest}`} replace />
}

/**
 * The Blazor client's Device Update History page (`/devices/{id}/updates`), which is the device
 * page's Update history tab here.
 */
function DeviceUpdatesRedirect() {
  const { deviceId = '' } = useParams()
  return <Navigate to={`/devices/${deviceId}?tab=updates`} replace />
}

/**
 * What answers a path this module does not serve.
 *
 * This replaces the Phase 0 scaffold — a token-system demonstration that was still the catch-all
 * when the ~65 real routes landed. Because the module mounted under a prefix its own routes did not
 * share, *every* page fell through to it, so the whole application rendered as a swatch of status
 * badges. Two faults compounding: a placeholder nobody removed, and a route table nothing walked.
 * `RelayRoutes.test.tsx` now walks it.
 */
function NotFound() {
  const { pathname } = useLocation()
  return (
    <div className="p-xl">
      <div className="mx-auto flex max-w-3xl flex-col gap-md">
        <h1 className="text-2xl font-semibold text-fg">Page Not Found</h1>
        <p className="text-sm text-fg-secondary">
          Nothing is served at <code className="font-mono text-fg">{pathname}</code>. Use the
          navigation to the left, or start from the{' '}
          <Link to="/" className="text-accent">
            dashboard
          </Link>
          .
        </p>
      </div>
    </div>
  )
}

export function RelayRoutes() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/connect/vnc/:deviceId" element={<VncPage />} />
        <Route path="/connect/ssh/:deviceId" element={<SshPage />} />
        {/*
          Both served by one component, which reads the path to decide which service it opens — the
          two differ by a service name and a render mode, and a second copy of the viewer would be
          two things to fix every time one breaks.
        */}
        <Route path="/connect/video/:deviceId" element={<VideoPage />} />
        <Route path="/connect/backstage/:deviceId" element={<VideoPage />} />
        {/* The management pages, which share the Connect chrome but open no session of their own. */}
        <Route path="/connect/processes/:deviceId" element={<ProcessPage />} />
        <Route path="/connect/files/:deviceId" element={<FilePage />} />
        <Route path="/connect/docker/:deviceId" element={<DockerPage />} />
        {/* The fleet itself, which every Connect page returns to via ?returnUrl=%2Fdevices. */}
        <Route path="/devices" element={<DevicesPage />} />
        <Route path="/sites" element={<SitesPage />} />
        <Route path="/devices/:deviceId" element={<DeviceDetailsPage />} />
        <Route path="/devices/:deviceId/updates" element={<DeviceUpdatesRedirect />} />
        {/* Groups. `/groups/new` is a fixed segment, ranked above `/groups/:groupId` by react-router. */}
        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/groups/new" element={<GroupEditorPage />} />
        <Route path="/groups/:groupId" element={<GroupDetailPage />} />
        <Route path="/groups/:groupId/edit" element={<GroupEditorPage />} />
        {/* Administration. Each page gates itself on its own permission via PageGate. */}
        <Route path="/admin/users" element={<UsersPage />} />
        <Route path="/admin/roles" element={<RolesPage />} />
        <Route path="/admin/organizations" element={<OrganizationsPage />} />
        <Route path="/admin/organizations/rehome" element={<RehomePage />} />
        <Route path="/admin/location-groups" element={<LocationGroupsPage />} />
        <Route path="/admin/brands" element={<BrandsPage />} />
        <Route path="/admin/device-types" element={<DeviceTypesPage />} />
        <Route path="/printers" element={<PrintersPage />} />
        <Route path="/admin/locations" element={<LocationsPage />} />
        <Route path="/admin/devices" element={<AdminDevicesPage />} />
        {/* The Administration page became a sidebar section; a bookmark of it lands on the dashboard. */}
        <Route path="/admin" element={<Navigate to="/" replace />} />
        <Route path="/admin/settings" element={<ServerSettingsPage />} />
        {/*
          Zero-touch provisioning. No parameterised routes: a claim's detail is `?claim=<id>` on the
          claims page and a device's timeline opens over the activity page, so each is linkable
          without a placeholder the e2e route walker would need to learn to fill.
        */}
        <Route path="/provisioning" element={<Navigate to="/provisioning/claims" replace />} />
        <Route path="/provisioning/add" element={<AddDevicesPage />} />
        <Route path="/downloads/support" element={<SupportDownloadsPage />} />
        <Route path="/provisioning/claims" element={<ClaimsPage />} />
        <Route path="/provisioning/expected" element={<RegistrationsPage />} />
        <Route path="/provisioning/queue" element={<QueuePage />} />
        <Route path="/provisioning/activity" element={<EnrollmentActivityPage />} />
        <Route path="/provisioning/pairing" element={<PairingPage />} />
        <Route path="/provisioning/type-rules" element={<TypeRulesPage />} />
        <Route path="/provisioning/api-keys" element={<ApiKeysPage />} />
        <Route path="/admin/api-keys" element={<IntegrationApiKeysPage />} />
        {/*
          The System Audit Log became Activity, which every part of the relay writes to. The old
          address redirects so bookmarks still land somewhere true.
        */}
        <Route path="/admin/audit" element={<Navigate to="/activity" replace />} />
        <Route path="/activity" element={<ActivityPage />} />
        {/* Alerts (Plans/mobile-app.md, Phase A): the inbox, one incident, my notifications, and setup. */}
        <Route path="/alerts" element={<AlertsPage />} />
        <Route path="/alerts/notifications" element={<NotificationSettingsPage />} />
        <Route path="/alerts/setup" element={<AlertingSetupPage />} />
        <Route path="/alerts/:id" element={<IncidentPage />} />
        <Route path="/admin/audit/authorization" element={<AuthorizationAuditLogPage />} />
        <Route path="/admin/authorization" element={<AuthorizationDashboardPage />} />
        {/*
          Ring templates became rollout plans, and maintenance windows became a setting in every
          profile (shown per store on the Locations page). The old addresses redirect.
        */}
        <Route path="/admin/ring-templates" element={<Navigate to="/rollout-plans" replace />} />
        <Route
          path="/admin/maintenance-windows"
          element={<Navigate to="/admin/locations?tab=windows" replace />}
        />
        {/*
          Settings profiles replaced Configuration Templates and Blueprints, and Compliance replaced
          the blueprint compliance dashboard. The old addresses redirect so bookmarks still land.
        */}
        <Route path="/settings/profiles" element={<SettingsProfilesPage />} />
        <Route path="/settings/profiles/:profileId" element={<ProfileEditorPage />} />
        <Route path="/settings/explorer" element={<SettingExplorerPage />} />
        <Route path="/insights/compliance" element={<CompliancePage />} />
        <Route path="/locations/:locationId" element={<LocationPage />} />
        <Route path="/admin/blueprints/*" element={<Navigate to="/settings/profiles" replace />} />
        <Route path="/admin/blueprints" element={<Navigate to="/settings/profiles" replace />} />
        <Route
          path="/admin/config-templates/*"
          element={<Navigate to="/settings/profiles" replace />}
        />
        <Route
          path="/admin/config-templates"
          element={<Navigate to="/settings/profiles" replace />}
        />
        {/*
          Jobs. The library is deliberately ungated — `ListJobsAsync` has no permission check and says
          why: the `job_roles` filter is the access control. The fixed segments are registered before
          `/jobs/:jobId` for readability only; react-router ranks by specificity, so `queue`, `runs`,
          `schedules`, `variables` and `approvals` cannot be swallowed by the parameterised route.
        */}
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/jobs/queue" element={<JobQueuePage />} />
        <Route path="/jobs/runs" element={<JobRunsPage />} />
        <Route path="/jobs/schedules" element={<JobSchedulesPage />} />
        <Route path="/jobs/variables" element={<JobVariablesPage />} />
        <Route path="/jobs/approvals" element={<JobApprovalsPage />} />
        <Route path="/jobs/:jobId" element={<JobEditorPage />} />
        {/*
          Firmware (the OS image library) and Windows updates (the catalog the fleet reports): what
          Firmware and Windows updates profiles choose from. They were one page with two tabs; the
          old tab, the OTA firmware pages, the Windows Update pages and /settings/software (the first
          name) redirect to whichever of the two they meant.
        */}
        <Route
          path="/settings/firmware"
          element={<FirmwareTabRedirect page={<FirmwarePage />} />}
        />
        <Route path="/settings/windows-updates" element={<WindowsUpdatesPage />} />
        <Route path="/settings/agent-versions" element={<AgentVersionsPage />} />
        <Route path="/settings/software" element={<FirmwareTabRedirect />} />
        <Route path="/ota/firmware/*" element={<Navigate to="/settings/firmware" replace />} />
        <Route path="/ota/firmware" element={<Navigate to="/settings/firmware" replace />} />
        <Route path="/updates/*" element={<Navigate to="/settings/windows-updates" replace />} />
        <Route path="/updates" element={<Navigate to="/settings/windows-updates" replace />} />
        {/*
          Rollouts: one engine for settings, firmware and updates, staged by rollout plans. The OTA
          rollouts, deployment rings, location rings and brand rollout pages it replaced redirect here.
        */}
        <Route path="/rollouts" element={<RolloutsPage />} />
        <Route path="/rollouts/:rolloutId" element={<RolloutDetailPage />} />
        <Route path="/rollout-plans" element={<RolloutPlansPage />} />
        <Route path="/ota" element={<Navigate to="/rollouts" replace />} />
        <Route path="/ota/rollouts/*" element={<Navigate to="/rollouts" replace />} />
        <Route path="/ota/rollouts" element={<Navigate to="/rollouts" replace />} />
        <Route path="/ota/rings-config" element={<Navigate to="/rollout-plans" replace />} />
        <Route path="/ota/rings" element={<Navigate to="/rollout-plans" replace />} />
        <Route path="/ota/location-rings" element={<Navigate to="/rollout-plans" replace />} />
        <Route path="/brand/:brandId/*" element={<Navigate to="/rollouts" replace />} />
        {/*
          System logs, deliberately ungated: access there is tenancy rather than a permission key.
          `system.admin` sees every device and everyone else is scoped in SQL to the devices they can
          reach, so the filter is the access control and a client gate would refuse operators the relay
          serves — the same reasoning as the job library.
        */}
        <Route path="/system-logs" element={<SystemLogsPage />} />
        {/*
          The device map, ungated for the same reason as the logs: the relay scopes what it returns to
          the caller's own sites. That scoping is new — the arm previously returned every location in
          the estate to anyone — and TenancyIsolationTests fails without it.
        */}
        <Route path="/map" element={<MapPage />} />
        {/*
          The dashboard is the Blazor app's "/" — registered on an explicit path here because this
          module's catch-all still answers the root, and because it is reached from the sidebar rather
          than by landing on it.
        */}
        <Route path="/dashboard" element={<DashboardPage />} />
        {/*
          And at the root, which is where the Blazor client's dashboard lived and where every
          "go home" link in this portal points.
        */}
        <Route path="/" element={<DashboardPage />} />
        {/*
          Sessions, ungated: the relay returns only the caller's own, with no administrator override,
          so the query itself is the access control.
        */}
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

export default RelayRoutes
